import { Button, cn } from '@1trading/react';
import { Eraser } from 'lucide-react';
import { type ReactElement, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, useMemo, useRef, useState } from 'react';
import {
  CHART_H,
  clampRecoveryPct,
  clientPointToSvg,
  computeScale,
  driverEnd,
  formatPct,
  formatPrice,
  isInChart,
  PAD,
  polylinePoints,
  priceAtPct,
  type Series,
  SVG_H,
  SVG_W,
  snapPct,
  snapTime,
  sortTimeline,
  svgToData,
  type TimelinePoint,
  toRecoveryTime,
  toX,
  toY,
} from './simulation-math';

/** Fixed amber (legacy `--recovery-color`) — must read as amber in both themes. */
const RECOVERY_COLOR = '#f9a825';
const RECOVERY_ZONE_FILL = 'rgba(249, 168, 37, 0.06)';
const DRIVER_COLOR = 'hsl(var(--primary))';
const SERIES_DOT_SIZE_PX = 12;

const TOOLTIP_WIDTH_WITH_PRICE = 160;
const TOOLTIP_WIDTH_NO_PRICE = 90;
const TOOLTIP_HEIGHT = 18;

const HIT_RADIUS = 20;
const HOVER_RING_RADIUS = 10;
const DOT_RADIUS = 5;

interface DragState {
  series: Series;
  /** index into the UNSORTED prop array (stable during a drag — parent only replaces on commit) */
  index: number;
  t: number;
  changePct: number;
}

interface HoverState {
  svgX: number;
  svgY: number;
  /** raw UNSNAPPED values */
  t: number;
  pct: number;
}

export interface SimulationTimelineEditorProps {
  /** source of truth (parent state) — fully controlled */
  driver: TimelinePoint[];
  recovery: TimelinePoint[];
  /** from the 5 s price poll; null hides all price UI */
  currentPrice?: number | null;
  /** always called with sorted + snapped points */
  onDriverChange: (points: TimelinePoint[]) => void;
  onRecoveryChange: (points: TimelinePoint[]) => void;
}

interface IndexedPoint extends TimelinePoint {
  index: number;
}

interface IndexedVisualPoint extends IndexedPoint {
  visualT: number;
}

const byTime = (a: TimelinePoint, b: TimelinePoint): number => a.t - b.t;

export const SimulationTimelineEditor = ({ driver, recovery, currentPrice = null, onDriverChange, onRecoveryChange }: SimulationTimelineEditorProps) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [mode, setMode] = useState<Series>('driver');
  const [hover, setHover] = useState<HoverState | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const didDragRef = useRef(false);
  const downOnPointRef = useRef(false);

  // Truthy on purpose: a 0 price hides price UI exactly like the legacy editor
  const hasPrice = Boolean(currentPrice);

  // render merges the drag draft over the prop point; parent arrays stay untouched until release
  const draftDriver = useMemo(() => (drag?.series === 'driver' ? driver.map((p, i) => (i === drag.index ? { t: drag.t, changePct: drag.changePct } : p)) : driver), [driver, drag]);
  const draftRecovery = useMemo(
    () => (drag?.series === 'recovery' ? recovery.map((p, i) => (i === drag.index ? { t: drag.t, changePct: drag.changePct } : p)) : recovery),
    [recovery, drag],
  );

  const sortedDriver: IndexedPoint[] = useMemo(() => draftDriver.map((p, index) => ({ ...p, index })).sort(byTime), [draftDriver]);

  // derived live, so editing the last driver point slides the whole recovery series
  const { driverEndTime, lastDriverPoint } = useMemo(() => driverEnd(draftDriver), [draftDriver]);

  const sortedRecoveryVisual: IndexedVisualPoint[] = useMemo(
    () =>
      draftRecovery
        .map((p, index) => ({ ...p, index }))
        .sort(byTime)
        .map((p) => ({ ...p, visualT: p.t + driverEndTime })),
    [draftRecovery, driverEndTime],
  );

  const scale = useMemo(
    () =>
      computeScale(
        [...draftDriver.map((p) => p.t), ...draftRecovery.map((p) => p.t + driverEndTime)],
        [...draftDriver.map((p) => p.changePct), ...draftRecovery.map((p) => p.changePct)],
        hasPrice,
      ),
    [draftDriver, draftRecovery, driverEndTime, hasPrice],
  );
  const { chartW, xMax, yExtent, xGridValues, yGridValues } = scale;

  const x = (t: number): number => toX(t, xMax, chartW);
  const y = (pct: number): number => toY(pct, yExtent);

  const emitSeries = (series: Series, points: TimelinePoint[]): void => {
    if (series === 'driver') onDriverChange(sortTimeline(points));
    else onRecoveryChange(sortTimeline(points));
  };

  // press on empty chart resets the swallow flags (point presses stopPropagation)
  const handleSvgPointerDown = (): void => {
    didDragRef.current = false;
    downOnPointRef.current = false;
  };

  // press on a point starts a drag; capture on the svg keeps it alive outside the element
  const handlePointPointerDown =
    (series: Series, index: number) =>
    (event: ReactPointerEvent<SVGGElement>): void => {
      event.stopPropagation();
      event.preventDefault();
      downOnPointRef.current = true;
      const point = (series === 'driver' ? driver : recovery)[index];
      if (!point) return;
      setDrag({ series, index, t: point.t, changePct: point.changePct });
      event.currentTarget.ownerSVGElement?.setPointerCapture(event.pointerId);
    };

  // drag frames snap but are NOT chart-bounds clamped (axis growth)
  const handleSvgPointerMove = (event: ReactPointerEvent<SVGSVGElement>): void => {
    const svg = svgRef.current;
    if (!svg) return;
    const { x: svgX, y: svgY } = clientPointToSvg(event.clientX, event.clientY, svg.getBoundingClientRect());
    if (drag) {
      didDragRef.current = true;
      const { t, pct } = svgToData(svgX, svgY, xMax, yExtent, chartW);
      setDrag(
        drag.series === 'driver'
          ? { ...drag, t: snapTime(t), changePct: snapPct(pct) }
          : { ...drag, t: toRecoveryTime(t, driverEndTime), changePct: clampRecoveryPct(snapPct(pct), lastDriverPoint.changePct) },
      );
      return;
    }
    if (isInChart(svgX, svgY, chartW)) {
      const { t, pct } = svgToData(svgX, svgY, xMax, yExtent, chartW);
      setHover({ svgX, svgY, t, pct });
    } else {
      setHover(null);
    }
  };

  // emit-on-release only (fires even for a zero-movement press, harmless no-op emit)
  const commitDrag = (): void => {
    if (!drag) return;
    const source = drag.series === 'driver' ? driver : recovery;
    const next = source.map((p, i) => (i === drag.index ? { t: drag.t, changePct: drag.changePct } : p));
    setDrag(null);
    emitSeries(drag.series, next);
  };

  // click-add, with the wasDragging/downOnPoint swallow semantics
  const handleSvgClick = (event: ReactMouseEvent<SVGSVGElement>): void => {
    if (didDragRef.current || downOnPointRef.current) {
      didDragRef.current = false;
      downOnPointRef.current = false;
      return;
    }
    const svg = svgRef.current;
    if (!svg) return;
    const { x: svgX, y: svgY } = clientPointToSvg(event.clientX, event.clientY, svg.getBoundingClientRect());
    if (!isInChart(svgX, svgY, chartW)) return;
    const { t, pct } = svgToData(svgX, svgY, xMax, yExtent, chartW);
    if (mode === 'driver') {
      emitSeries('driver', [...driver, { t: snapTime(t), changePct: snapPct(pct) }]);
    } else {
      emitSeries('recovery', [...recovery, { t: toRecoveryTime(t, driverEndTime), changePct: clampRecoveryPct(snapPct(pct), lastDriverPoint.changePct) }]);
    }
  };

  // right-click a point deletes it; background right-click keeps the browser menu
  const handlePointContextMenu =
    (series: Series, index: number) =>
    (event: ReactMouseEvent<SVGGElement>): void => {
      event.preventDefault();
      event.stopPropagation();
      // A concurrent delete would shrink the array under an active drag's index
      // and make the release overwrite the wrong point (review finding)
      if (drag) return;
      const source = series === 'driver' ? driver : recovery;
      emitSeries(
        series,
        source.filter((_, i) => i !== index),
      );
    };

  // eraser clears both series
  const handleClear = (): void => {
    onDriverChange([]);
    onRecoveryChange([]);
  };

  const handleSvgPointerLeave = (): void => {
    if (!drag) setHover(null);
  };

  const firstDriver = sortedDriver[0];
  const lastSortedDriver = sortedDriver.at(-1);
  const firstRecovery = sortedRecoveryVisual[0];

  const renderPoint = (series: Series, point: IndexedPoint, visualX: number): ReactElement => {
    const color = series === 'driver' ? DRIVER_COLOR : RECOVERY_COLOR;
    const isDraggingThis = drag?.series === series && drag.index === point.index;
    return (
      /* biome-ignore lint/a11y/noStaticElementInteractions: bespoke pointer-driven chart editor (mouse/touch only) */
      <g
        className={cn('group', isDraggingThis ? 'cursor-grabbing' : 'cursor-grab')}
        key={point.index}
        onClick={(event) => event.stopPropagation()}
        onContextMenu={handlePointContextMenu(series, point.index)}
        onPointerDown={handlePointPointerDown(series, point.index)}
        transform={`translate(${visualX}, ${y(point.changePct)})`}
      >
        <circle fill="transparent" r={HIT_RADIUS} />
        <circle className="opacity-0 transition-opacity duration-150 group-hover:opacity-[0.15]" fill={color} r={HOVER_RING_RADIUS} />
        {/* dot grows r=5 → r=7 on hover via CSS scale (5 × 1.4 = 7) so it transitions smoothly */}
        <circle
          className="origin-center transition-transform duration-150 group-hover:scale-[1.4]"
          fill={color}
          r={DOT_RADIUS}
          stroke="#fff"
          strokeWidth={2}
          style={{ transformBox: 'fill-box', filter: 'drop-shadow(0 1px 2px rgb(0 0 0 / 0.3))' }}
        />
        {/* ⚠️ recovery labels show the RELATIVE t, not the visual time */}
        <text className="pointer-events-none" fill="hsl(var(--foreground) / 0.7)" fontSize={10} fontWeight={700} textAnchor="middle" y={-14}>
          {point.t}m / {formatPct(point.changePct)}
        </text>
        {hasPrice ? (
          <text className="pointer-events-none" fill="hsl(var(--foreground) / 0.5)" fontSize={11} textAnchor="middle" y={18}>
            {formatPrice(priceAtPct(point.changePct, currentPrice))}
          </text>
        ) : null}
      </g>
    );
  };

  return (
    <div>
      {/* toolbar */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex overflow-hidden rounded-md border">
          <button
            aria-pressed={mode === 'driver'}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
              mode === 'driver' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted/50',
            )}
            onClick={() => setMode('driver')}
            type="button"
          >
            <span className="rounded-full" style={{ width: SERIES_DOT_SIZE_PX, height: SERIES_DOT_SIZE_PX, backgroundColor: DRIVER_COLOR }} />
            Driver
          </button>
          <button
            aria-pressed={mode === 'recovery'}
            className={cn(
              'flex items-center gap-1.5 border-l px-3 py-1.5 text-xs font-medium transition-colors',
              mode === 'recovery' ? 'bg-[#f9a825]/10 text-[#f9a825]' : 'text-muted-foreground hover:bg-muted/50',
            )}
            onClick={() => setMode('recovery')}
            type="button"
          >
            <span className="rounded-full" style={{ width: SERIES_DOT_SIZE_PX, height: SERIES_DOT_SIZE_PX, backgroundColor: RECOVERY_COLOR }} />
            Recovery
          </button>
        </div>
        <div className="flex-1" />
        {currentPrice ? <span className="text-xs text-muted-foreground">Mid: {formatPrice(currentPrice)}</span> : null}
        <span className="text-xs text-muted-foreground">Click to add · Drag to move · Right-click to remove</span>
        <Button aria-label="Clear all points" disabled={driver.length === 0 && recovery.length === 0} onClick={handleClear} size="icon" type="button" variant="ghost">
          <Eraser className="size-4" />
        </Button>
      </div>

      {/* the chart. Paint order below matters. */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only interaction model ported from the legacy mouse-only editor */}
      <svg
        aria-label="Simulation price timeline editor"
        className="block h-auto w-full cursor-crosshair touch-none select-none rounded-lg border"
        onClick={handleSvgClick}
        onPointerCancel={commitDrag}
        onPointerDown={handleSvgPointerDown}
        onPointerLeave={handleSvgPointerLeave}
        onPointerMove={handleSvgPointerMove}
        onPointerUp={commitDrag}
        ref={svgRef}
        role="application"
        style={{ borderColor: 'hsl(var(--border) / 0.2)' }}
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
      >
        {/* 1. plot background */}
        <rect fill="hsl(var(--muted) / 0.15)" height={CHART_H} width={chartW} x={PAD.left} y={PAD.top} />

        {/* 2. recovery zone tint */}
        {driverEndTime > 0 && sortedRecoveryVisual.length > 0 ? (
          <rect fill={RECOVERY_ZONE_FILL} height={CHART_H} width={Math.max(0, PAD.left + chartW - x(driverEndTime))} x={x(driverEndTime)} y={PAD.top} />
        ) : null}

        {/* 3. horizontal grid + left axis labels */}
        {yGridValues.map((pct) => (
          <g key={`y-${pct}`}>
            <line
              stroke={pct === 0 ? 'hsl(var(--foreground) / 0.4)' : 'hsl(var(--foreground) / 0.15)'}
              strokeDasharray={pct === 0 ? undefined : '4 2'}
              strokeWidth={pct === 0 ? 1.5 : 0.5}
              x1={PAD.left}
              x2={PAD.left + chartW}
              y1={y(pct)}
              y2={y(pct)}
            />
            <text fill="hsl(var(--foreground) / 0.45)" fontSize={11} textAnchor="end" x={PAD.left - 8} y={y(pct) + 4}>
              {pct > 0 ? '+' : ''}
              {pct}%
            </text>
          </g>
        ))}

        {/* 4. price column */}
        {hasPrice
          ? yGridValues.map((pct) => (
              <text
                fill={pct === 0 ? 'hsl(var(--foreground) / 0.7)' : 'hsl(var(--foreground) / 0.35)'}
                fontSize={11}
                fontWeight={pct === 0 ? 700 : undefined}
                key={`price-${pct}`}
                textAnchor="start"
                x={PAD.left + chartW + 8}
                y={y(pct) + 4}
              >
                {formatPrice(priceAtPct(pct, currentPrice))}
              </text>
            ))
          : null}

        {/* 5. vertical grid + bottom axis labels (absolute minutes) */}
        {xGridValues.map((t) => (
          <g key={`x-${t}`}>
            <line stroke="hsl(var(--foreground) / 0.15)" strokeDasharray="4 2" strokeWidth={0.5} x1={x(t)} x2={x(t)} y1={PAD.top} y2={PAD.top + CHART_H} />
            <text fill="hsl(var(--foreground) / 0.45)" fontSize={11} textAnchor="middle" x={x(t)} y={PAD.top + CHART_H + 16}>
              {t}m
            </text>
          </g>
        ))}

        {/* 6. driver-end divider */}
        {driverEndTime > 0 ? (
          <line opacity={0.4} stroke={DRIVER_COLOR} strokeDasharray="6 3" strokeWidth={1} x1={x(driverEndTime)} x2={x(driverEndTime)} y1={PAD.top} y2={PAD.top + CHART_H} />
        ) : null}

        {/* 7. driver lead-in — price holds at the first level until the first point */}
        {firstDriver ? (
          <line
            stroke="hsl(var(--primary) / 0.3)"
            strokeDasharray="4 3"
            strokeWidth={1.5}
            x1={PAD.left}
            x2={x(firstDriver.t)}
            y1={y(firstDriver.changePct)}
            y2={y(firstDriver.changePct)}
          />
        ) : null}

        {/* 8. driver polyline */}
        {sortedDriver.length >= 2 ? (
          <polyline
            fill="none"
            points={polylinePoints(sortedDriver.map((p) => ({ x: x(p.t), y: y(p.changePct) })))}
            stroke={DRIVER_COLOR}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
          />
        ) : null}

        {/* 9. fade-out extension — hints a drift back to baseline when there is no recovery */}
        {lastSortedDriver && sortedRecoveryVisual.length === 0 ? (
          <line
            stroke="hsl(var(--foreground) / 0.15)"
            strokeDasharray="4 3"
            strokeWidth={1.5}
            x1={x(lastSortedDriver.t)}
            x2={PAD.left + chartW}
            y1={y(lastSortedDriver.changePct)}
            y2={y(0)}
          />
        ) : null}

        {/* 10. recovery connector */}
        {lastSortedDriver && firstRecovery ? (
          <line
            opacity={0.5}
            stroke={RECOVERY_COLOR}
            strokeDasharray="4 2"
            strokeWidth={1.5}
            x1={x(driverEndTime)}
            x2={x(firstRecovery.visualT)}
            y1={y(lastDriverPoint.changePct)}
            y2={y(firstRecovery.changePct)}
          />
        ) : null}

        {/* 11. recovery polyline */}
        {sortedRecoveryVisual.length >= 2 ? (
          <polyline
            fill="none"
            points={polylinePoints(sortedRecoveryVisual.map((p) => ({ x: x(p.visualT), y: y(p.changePct) })))}
            stroke={RECOVERY_COLOR}
            strokeDasharray="8 4"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
          />
        ) : null}

        {/* 12. driver points, then recovery points */}
        {sortedDriver.map((p) => renderPoint('driver', p, x(p.t)))}
        {sortedRecoveryVisual.map((p) => renderPoint('recovery', p, x(p.visualT)))}

        {/* 13. crosshair + tooltip — hover only, never while dragging */}
        {hover && !drag ? (
          <g pointerEvents="none">
            <line stroke="hsl(var(--foreground) / 0.2)" strokeWidth={0.5} x1={hover.svgX} x2={hover.svgX} y1={PAD.top} y2={PAD.top + CHART_H} />
            <line stroke="hsl(var(--foreground) / 0.2)" strokeWidth={0.5} x1={PAD.left} x2={PAD.left + chartW} y1={hover.svgY} y2={hover.svgY} />
            <rect
              fill="hsl(var(--foreground) / 0.85)"
              height={TOOLTIP_HEIGHT}
              opacity={0.9}
              rx={3}
              width={hasPrice ? TOOLTIP_WIDTH_WITH_PRICE : TOOLTIP_WIDTH_NO_PRICE}
              x={hover.svgX + 6}
              y={hover.svgY - 20}
            />
            <text fill="hsl(var(--background))" fontSize={11} x={hover.svgX + 10} y={hover.svgY - 7}>
              {hover.t.toFixed(0)}m / {formatPct(hover.pct)}
              {hasPrice ? ` → ${formatPrice(priceAtPct(hover.pct, currentPrice))}` : ''}
            </text>
          </g>
        ) : null}
      </svg>
    </div>
  );
};
SimulationTimelineEditor.displayName = 'SimulationTimelineEditor';
